import { DateTime, Interval } from "luxon"

/**
 * Gets the most recent month date range within the bounds
 *
 * @param bounds - The date range to verify against
 * @returns - The most recent month date range within the bounds
 * @example
 * getMostRecentMonthDateRange({
 *   start: DateTime.fromISO("2021-01-01"),
 *   end: DateTime.fromISO("2022-12-31"),
 * })
 */
export const getMostRecentMonthDateRange = (bounds: Interval): Interval => {
  if (!bounds.isValid || !bounds.start || !bounds.end) {
    return Interval.invalid("Invalid bounds")
  }

  const mostRecentMonthStart = bounds.end.startOf("month")

  const mostRecentMonthEnd = mostRecentMonthStart.endOf("month")
  const mostRecentMonthDateRange = Interval.fromDateTimes(
    mostRecentMonthStart,
    mostRecentMonthEnd
  )

  return (
    mostRecentMonthDateRange.intersection(bounds) ??
    Interval.invalid("Invalid bounds")
  )
}

/**
 * Gets the most recent previous year date range within the bounds
 *
 * @param bounds - The date range to verify against
 * @returns - The most recent previous year date range within the bounds
 * @example
 * getMostRecentPreviousYearDateRange({
 *   start: DateTime.fromISO("2013-03-01"),
 *   end: DateTime.fromISO("2022-12-31"),
 * })
 */
export const getMostRecentPreviousYearDateRange = (
  bounds: Interval
): Interval => {
  if (!bounds.isValid || !bounds.start || !bounds.end) {
    return Interval.invalid("Invalid bounds")
  }

  const mostRecentPreviousYearStart = DateTime.min(
    DateTime.now().minus({ year: 1 }).startOf("year"),
    bounds.end.startOf("year")
  )

  bounds.end.minus({ year: 1 }).startOf("year")
  const mostRecentPreviousYearEnd = mostRecentPreviousYearStart.endOf("year")
  const mostRecentPreviousYearDateRange = Interval.fromDateTimes(
    mostRecentPreviousYearStart,
    mostRecentPreviousYearEnd
  )

  return (
    mostRecentPreviousYearDateRange.intersection(bounds) ??
    Interval.invalid("Invalid bounds")
  )
}

/**
 * Determines if the date range is valid and within the bounds
 *
 * @param dateRange - The date range to verify
 * @param bounds - The date range to verify against
 * @returns - Whether the date range is valid and within the bounds
 * @example
 * isValidDateTimeRange(dateRange, bounds)
 */
export const isValidDateTimeRange = (
  dateRange: Interval,
  bounds: Interval
): boolean => {
  return dateRange.isValid && bounds.engulfs(dateRange)
}
